import { useCallback } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';

/**
 * "← Back to chat" (CEL-3662 X1). A chip or a connect card that opens a setup page (/cowork,
 * /connectors/tools, ...) adds `back=<the chat's path>` to the link; the shared page header shows
 * the back link only when that value is a SAME-ORIGIN RELATIVE path. Anything else (another host,
 * a scheme, a protocol-relative `//host`, a backslash trick) renders nothing: the page stays exactly
 * as it is without an origin (sidebar, email, another agent's link).
 */
export const BACK_PARAM = 'back';
const MAX_BACK_LENGTH = 512;

/** The value when it is a safe in-app path ("/chat/abc", "/ai_employee/x?y=1"), else null. */
export const safeBackPath = (value) => {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  if (!v || v.length > MAX_BACK_LENGTH) return null;
  if (!v.startsWith('/')) return null; // relative only: no scheme, no "javascript:", no bare host
  if (v.startsWith('//')) return null; // protocol-relative = another host
  if (v.includes('\\')) return null; // browsers read "/\evil.com" as "//evil.com"
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(v)) return null; // control characters (tab/newline tricks)
  return v;
};

// A chat page: a regular chat (/chat/<id>) or an AI employee's chat (/ai_employee/<id>, not its
// /profile or /share pages). Only these earn a "Back to chat" link.
const CHAT_PATH_RE = /^\/(chat\/[^/?#]+|ai_employee\/[^/?#]+)\/?$/;
export const isChatPath = (pathname) => CHAT_PATH_RE.test(String(pathname || ''));

/**
 * The back path a page may offer as "Back to chat": a safe path (above) that is also a CHAT path, query
 * allowed (review N4: `?back=/settings` is same-origin but not a chat, so no link). Else null.
 */
export const safeChatBackPath = (value) => {
  const v = safeBackPath(value);
  return v && isChatPath(v.split(/[?#]/)[0]) ? v : null;
};

/** The chat back path carried by a location search string ("?back=..."), else null. */
export const backPathFromSearch = (search) => {
  try {
    return safeChatBackPath(new URLSearchParams(search || '').get(BACK_PARAM));
  } catch {
    return null;
  }
};

/**
 * The chat the user is on right now (path + query), the value a chip or card carries; null when the
 * page is not a chat (the new-chat page, a setup page), so no back link is promised.
 */
export const currentChatPath = () => {
  try {
    const { pathname, search } = window.location;
    return isChatPath(pathname) ? `${pathname}${search || ''}` : null;
  } catch {
    return null;
  }
};

/**
 * `href` with `back=<backPath>` added. Works for in-app paths ("/cowork") and absolute cellcog URLs
 * (the card fallback tile); keeps the existing query and hash. An unsafe or empty backPath returns
 * `href` unchanged.
 */
export const withBack = (href, backPath = currentChatPath()) => {
  const back = safeBackPath(backPath);
  if (!href || !back) return href;
  try {
    const absolute = /^https?:\/\//i.test(href);
    const url = new URL(href, 'https://cellcog.ai');
    url.searchParams.set(BACK_PARAM, back);
    return absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
};

/**
 * Navigate inside the app (react-router, no full reload) when a router is mounted; a plain location
 * change otherwise (a component rendered outside the router, e.g. in a unit test). The router check
 * is stable for a component's life, so the conditional hook call never changes order.
 */
export const useInAppNavigate = () => {
  const inRouter = useInRouterContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const navigate = inRouter ? useNavigate() : null;
  return useCallback(
    (to) => {
      if (navigate) navigate(to);
      else window.location.assign(to);
    },
    [navigate]
  );
};
